<script setup>
import { onMounted, watch, ref} from 'vue'
import { useI18n } from 'vue-i18n'
import { formatTo12Hour } from '@/lib/date_helper.js'
import { getServiceProviderHours} from "@/lib/api/service_providers.js";

const props = defineProps({
  serviceProvider: {
    type: Object,
    required: true
  }
})

// I18n
const { t } = useI18n()

const test_data = ref({
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: []
})

const businessHours = ref([])
const businessStatus = ref(null)

function formatTime(day_data) {
  if (!day_data) {
    return t('service_providers.business_hours.closed')
  } else if (!day_data.end) {
    return t('service_providers.business_hours.open_24_hours')
  } else {
    let start_string = formatTo12Hour(day_data.start)
    let end_string = formatTo12Hour(day_data.end)
    return `${start_string} - ${end_string}`
  }
}

function initializeBusinessHours() {
  if (props.serviceProvider.id) {
    getServiceProviderHours(props.serviceProvider.id)
        .then((response) => {
          businessStatus.value = {
            isOpen: response.is_open,
            closedWeek: false,
            status: response.status,
            timezone: response.timezone,
            dayKey: response.next_working_day,
            closingTime: response.next_closing_time,
            openingTime: response.next_opening_time,
          }
          businessHours.value = response.hours;
        })
  }
}

onMounted(async () => {
  if (props.serviceProvider) {
    initializeBusinessHours();
  }
})

watch(() => props.serviceProvider, (newVal) => {
  // Handle prop change
  if (newVal.id) {
    initializeBusinessHours();
  }
})

</script>

<template>
  <div>
    <p v-if="businessStatus" class="mb-2 current-status">
      <span class="mr-2 uppercase">
        <span class="open" v-if="businessStatus.isOpen">{{ t('service_providers.business_hours.open') }}</span>
        <span class="closed" v-else>{{ t('service_providers.business_hours.closed') }}</span>
      </span>

      <span>
        <span v-if="businessStatus.isOpen">
          {{ t('service_providers.business_hours.closes_at', { time: businessStatus.closingTime }) }}
        </span>

        <span v-if="!businessStatus.isOpen && !businessStatus.closedWeek">
          <br />
          {{ t('service_providers.business_hours.opens_day_at', {
            day: t(`service_providers.business_hours.day_labels.${businessStatus.dayKey}`),
            time: businessStatus.openingTime }) }}
        </span>
        <span v-else-if="!businessStatus.isOpen && businessStatus.closedWeek">
          {{ t('service_providers.business_hours.closed_all_week') }}
        </span>
      </span>
    </p>
    <p v-else class="mb-2 current-status">
      <span class="mr-2 uppercase">Unknown</span>
    </p>

    <p v-if="businessStatus" class="mb-1 times-disclaimer text-[9.6px]">{{ t('service_providers.business_hours.times_disclaimer') }} ({{ businessStatus.timezone}})</p>
    <dl class="grid grid-cols-2 grid-cols-[80px_1fr]">
      <template v-if="businessHours.length" v-for="day in businessHours">
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.${day.day}`) }}:</dt>
        <dd class="uppercase flex flex-col" v-if="day.open_hours !== 'Closed'">
          {{ day.open_hours }} - {{ day.close_hours }}
        </dd>
        <dd class="uppercase" v-else>Closed</dd>
      </template>
      <template v-else>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.monday`) }}:</dt><dd>-</dd>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.tuesday`) }}:</dt><dd>-</dd>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.wednesday`) }}:</dt><dd>-</dd>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.thursday`) }}:</dt><dd>-</dd>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.friday`) }}:</dt><dd>-</dd>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.saturday`) }}:</dt><dd>-</dd>
        <dt class="w-24">{{ t(`service_providers.business_hours.day_labels_short.sunday`) }}:</dt><dd>-</dd>
      </template>
    </dl>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/service_provider/business_hours.scss';
</style>
